/**
 * L.1: URL → selection store sync.
 *
 * Mounts once at the app shell. Watches `location.pathname` and
 * mirrors the parent-id chain into `useActiveContextStore`. This
 * covers:
 *   - Cold loads (user opens a deep-link URL in a new tab)
 *   - Browser back/forward (popstate-driven URL changes)
 *   - Programmatic navigate() calls that don't pass through the
 *     workspace tree click handlers
 *
 * The short `/cases/:caseId` URL can't be resolved here (parent
 * ids come from `case.locate`); `<CaseRouteResolver>` owns the
 * final `selectCase()` call for that route after it resolves.
 *
 * See: ai-docs/lexide-layout-plan.md §4.4
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useActiveContextStore } from "@/stores/useActiveContextStore";

const LONG_CASE = /^\/clients\/([^/]+)\/matters\/([^/]+)\/cases\/([^/]+)/;
const MATTER = /^\/clients\/([^/]+)\/matters\/([^/]+)/;
const CLIENT = /^\/clients\/([^/]+)/;
const SHORT_CASE = /^\/cases\/[^/]+/;
// Dev-only probe routes drive selection themselves (via query params
// or direct store calls); skip the URL sync so it doesn't clobber.
const PROBE = /^\/__probe\//;

export function SelectionUrlSync() {
  const pathname = useLocation().pathname;
  const selectClient = useActiveContextStore((s) => s.selectClient);
  const selectMatter = useActiveContextStore((s) => s.selectMatter);
  const selectCase = useActiveContextStore((s) => s.selectCase);
  const clear = useActiveContextStore((s) => s.clear);

  useEffect(() => {
    // Dev-only probe routes are opaque to the sync — they own their
    // own selection (typically via query params).
    if (PROBE.test(pathname)) {
      return;
    }

    const longCaseMatch = LONG_CASE.exec(pathname);
    if (longCaseMatch) {
      selectCase(longCaseMatch[1], longCaseMatch[2], longCaseMatch[3]);
      return;
    }

    // Short case URL — parents unknown until CaseRouteResolver
    // finishes case.locate. It will call selectCase() itself.
    if (SHORT_CASE.test(pathname)) {
      return;
    }

    const matterMatch = MATTER.exec(pathname);
    if (matterMatch) {
      selectMatter(matterMatch[1], matterMatch[2]);
      return;
    }

    const clientMatch = CLIENT.exec(pathname);
    if (clientMatch) {
      selectClient(clientMatch[1]);
      return;
    }

    // Any other route (/home, /workspace, /research, ...) —
    // the user is no longer inside the hierarchy.
    clear();
  }, [pathname, selectClient, selectMatter, selectCase, clear]);

  return null;
}
